import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Chip,
  Grid,
  IconButton,
  Collapse
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { useUser } from '../contexts/UserContext';
import { useAccesos } from '../contexts/AccesosContext';

const ModalEditarTransacciones = ({ open, onClose, cierre, transacciones, loading, server }) => {
  const [editando, setEditando] = useState(false);
  const [transaccionesEditadas, setTransaccionesEditadas] = useState([]);
  const [tiposTransacciones, setTiposTransacciones] = useState([]);
  const [loadingTipos, setLoadingTipos] = useState(false);
  const [gruposExpandidos, setGruposExpandidos] = useState({});
  const { user } = useUser();
  const { tienePermiso } = useAccesos();

  useEffect(() => {
    console.log('🎯 Modal props actualizadas:', {
      open,
      cierre,
      transaccionesLength: transacciones?.length || 0,
      loading,
      server
    });
  }, [open, cierre, transacciones, loading, server]);

  useEffect(() => {
    if (open) {
      cargarTiposTransacciones();
    }
  }, [open]);

  const cargarTiposTransacciones = async () => {
    setLoadingTipos(true);
    try {
      console.log('🔄 Cargando tipos de transacciones desde:', `${server}/MgCajaTipoTransaccions`);
      const token = localStorage.getItem('token');
      const response = await fetch(`${server}/MgCajaTipoTransaccions`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('📦 Datos recibidos:', data);
        const tiposArray = data.$values || data || [];
        console.log('📋 Tipos de transacciones procesados:', tiposArray);
        setTiposTransacciones(tiposArray);
        
        if (tiposArray.length === 0) {
          console.warn('⚠️ No se encontraron tipos de transacciones');
        }
      } else {
        console.error('❌ Error en response:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('❌ Error al cargar tipos de transacciones:', error);
    } finally {
      setLoadingTipos(false);
    }
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined) return 'L. 0.00';
    return new Intl.NumberFormat('es-HN', {
      style: 'currency',
      currency: 'HNL'
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('es-HN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const handleIniciarEdicion = () => {
    console.log('🔧 Iniciando edición');
    console.log('📋 Tipos disponibles:', tiposTransacciones);
    console.log('💰 Transacciones a editar:', transacciones);
    
    if (!tiposTransacciones || tiposTransacciones.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Tipos de transacciones no disponibles',
        text: 'No se pueden cargar los tipos de transacciones. Intente nuevamente.',
      });
      return;
    }
    
    if (!transacciones || transacciones.length === 0) {
      Swal.fire({
        icon: 'info',
        title: 'Sin transacciones',
        text: 'No hay transacciones para editar en este cierre.',
      });
      return;
    }
    
    setTransaccionesEditadas(JSON.parse(JSON.stringify(transacciones)));
    setEditando(true);
  };

  const handleCancelarEdicion = () => {
    Swal.fire({
      icon: 'warning',
      title: '¿Cancelar edición?',
      text: 'Los cambios no guardados se perderán',
      showCancelButton: true,
      confirmButtonText: 'Sí, cancelar',
      cancelButtonText: 'No'
    }).then((result) => {
      if (result.isConfirmed) {
        setEditando(false);
        setTransaccionesEditadas([]);
      }
    });
  };

  const handleActualizarTransaccion = (index, campo, valor) => {
    const nuevasTransacciones = [...transaccionesEditadas];
    nuevasTransacciones[index] = {
      ...nuevasTransacciones[index],
      [campo]: valor
    };
    
    // Si cambia el tipo de transacción, actualizar la descripción
    if (campo === 'IdTipoTransaccion' || campo === 'idTipoTransaccion') {
      const tipoEncontrado = tiposTransacciones.find(t => 
        t.idTipoTransaccion === parseInt(valor) || t.IdTipoTransaccion === parseInt(valor)
      );
      if (tipoEncontrado) {
        nuevasTransacciones[index].DescripcionTipoTransaccion = 
          tipoEncontrado.descripcionTipoTransaccion || tipoEncontrado.DescripcionTipoTransaccion;
      }
    }
    
    setTransaccionesEditadas(nuevasTransacciones);
  };

  const validarTotalTransacciones = () => {
    const totalOriginal = transacciones.reduce((sum, t) => 
      sum + parseFloat(t.Valor || t.valor || 0), 0
    );
    const totalEditado = transaccionesEditadas.reduce((sum, t) => 
      sum + parseFloat(t.Valor || t.valor || 0), 0
    );
    
    return Math.abs(totalOriginal - totalEditado) < 0.01;
  };

  const handleGuardarCambios = async () => {
    // Verificar si el cierre ya fue cerrado y es de día anterior
    const esCierreCerrado = cierre?.fechaCierre && new Date(cierre.fechaCierre).getFullYear() > 1900;
    const fechaCierre = cierre?.fechaCierre ? new Date(cierre.fechaCierre) : null;
    const esDiaAnterior = fechaCierre && fechaCierre < new Date(new Date().setHours(0, 0, 0, 0));
    
    let htmlAdvertencia = `
      <p><strong>Al modificar las transacciones de este cierre:</strong></p>
      <ul style="text-align: left; margin: 10px 40px;">
        <li>Se actualizarán los movimientos de caja</li>
        <li>Los totales del sistema serán recalculados</li>`;
    
    if (esCierreCerrado && esDiaAnterior) {
      htmlAdvertencia += `
        <li style="color: red;"><strong>⚠️ IMPORTANTE: Este cierre ya fue cerrado y es de un día anterior</strong></li>
        <li style="color: red;"><strong>Deberá volver a ejecutar la Partida Automática de Cierre Diario</strong></li>
        <li style="color: red;">Esto afectará la contabilización registrada</li>`;
    } else if (esCierreCerrado) {
      htmlAdvertencia += `
        <li style="color: orange;"><strong>Este cierre ya fue cerrado</strong></li>
        <li>Deberá volver a ejecutar la Partida Automática de Cierre Diario</li>`;
    }
    
    htmlAdvertencia += `
      </ul>
      <p>¿Está seguro de continuar?</p>
    `;

    const resultado = await Swal.fire({
      icon: 'warning',
      title: '⚠️ Confirmar Modificación',
      html: htmlAdvertencia,
      showCancelButton: true,
      confirmButtonText: 'Sí, guardar cambios',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: esCierreCerrado && esDiaAnterior ? '#d33' : '#f39c12',
      cancelButtonColor: '#3085d6',
      width: '600px'
    });

    if (!resultado.isConfirmed) {
      return;
    }

    try {
      let errores = 0;
      const token = localStorage.getItem('token');
      
      for (const transaccion of transaccionesEditadas) {
        const transaccionId = transaccion.Id || transaccion.id;
        const payload = {
          id: transaccionId,
          idTipoTransaccion: parseInt(transaccion.IdTipoTransaccion || transaccion.idTipoTransaccion),
          valor: parseFloat(transaccion.Valor || transaccion.valor),
          numeroDocumento: transaccion.NumeroDocumento || transaccion.numeroDocumento,
          ingresadoPor: transaccion.IngresadoPor || transaccion.ingresadoPor,
          idEncabezado: transaccion.IdEncabezado || transaccion.idEncabezado,
          idFactura: transaccion.IdFactura || transaccion.idFactura,
          concepto: transaccion.Concepto || transaccion.concepto,
          modificadoPor: user?.codigoUsuario || 'ADMIN               ',
          fechaModificacion: new Date().toISOString()
        };

        try {
          const response = await fetch(`${server}/MgCajaArqueoDetalleSistemas/${transaccionId}`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
          });

          if (!response.ok) {
            console.error(`Error al actualizar transacción ${transaccionId}`);
            errores++;
          }
        } catch (error) {
          console.error(`Error al actualizar transacción ${transaccionId}:`, error);
          errores++;
        }
      }

      if (errores === 0) {
        let mensajeExito = `
          <p>Las transacciones se han actualizado correctamente.</p>
        `;
        
        if (esCierreCerrado) {
          mensajeExito += `
            <p style="color: orange;"><strong>📋 Recordatorio Importante:</strong></p>
            <p>Debe ejecutar la <strong>Partida Automática de Cierre Diario</strong> nuevamente para actualizar la contabilización.</p>
          `;
        }
        
        await Swal.fire({
          icon: 'success',
          title: 'Cambios guardados',
          html: mensajeExito,
          confirmButtonText: 'Entendido',
          width: '500px'
        });
        
        setEditando(false);
        setTransaccionesEditadas([]);
        onClose(true); // Pasar true para indicar que hubo cambios
      } else {
        Swal.fire({
          icon: 'warning',
          title: 'Guardado con errores',
          text: `Se guardaron los cambios pero ${errores} transacción(es) tuvieron error.`,
        });
      }
    } catch (error) {
      console.error('Error al guardar transacciones:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudieron guardar los cambios'
      });
    }
  };

  // Función para agrupar transacciones por tipo
  const agruparTransacciones = (transacciones) => {
    if (!transacciones || transacciones.length === 0) return [];
    
    const grupos = {};
    
    transacciones.forEach((trans, index) => {
      const idTipo = trans.IdTipoTransaccion || trans.idTipoTransaccion;
      const descripcion = trans.DescripcionTipoTransaccion || trans.descripcionTipoTransaccion;
      
      if (!grupos[idTipo]) {
        grupos[idTipo] = {
          idTipo,
          descripcion,
          total: 0,
          detalles: []
        };
      }
      
      grupos[idTipo].total += parseFloat(trans.Valor || trans.valor || 0);
      grupos[idTipo].detalles.push({ ...trans, indexOriginal: index });
    });
    
    return Object.values(grupos);
  };
  
  const toggleGrupo = (idTipo) => {
    setGruposExpandidos(prev => ({
      ...prev,
      [idTipo]: !prev[idTipo]
    }));
  };

  const transaccionesActuales = editando ? transaccionesEditadas : transacciones;

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (editando) {
          handleCancelarEdicion();
        } else {
          onClose();
        }
      }}
      maxWidth="xl"
      fullWidth
    >
      <DialogTitle sx={{ 
        backgroundColor: '#1976d2', 
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        <EditIcon />
        Editar Transacciones del Cierre - {cierre?.colaborador}
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        {/* Información del cierre */}
        <Paper sx={{ p: 2, mb: 3, backgroundColor: '#f5f5f5' }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="text.secondary">
                <strong>Usuario:</strong>
              </Typography>
              <Typography variant="body1">{cierre?.colaborador}</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="text.secondary">
                <strong>Sucursal:</strong>
              </Typography>
              <Typography variant="body1">{cierre?.sucursal}</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="text.secondary">
                <strong>Fecha:</strong>
              </Typography>
              <Typography variant="body1">{formatDate(cierre?.fechaInicio)}</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="text.secondary">
                <strong>ID Cierre:</strong>
              </Typography>
              <Typography variant="body1">#{cierre?.id}</Typography>
            </Grid>
          </Grid>
        </Paper>

        {!editando && (
          <Alert severity="info" sx={{ mb: 2 }}>
            📝 Puede editar las transacciones y mover montos entre ellos. El total debe mantenerse igual.
          </Alert>
        )}

        {editando && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>⚙️ Modo de edición activo.</strong> Los cambios afectarán el Detalle Sistema. El Detalle Usuario (lo reportado en el cierre) se mantiene para referencia. Después de guardar, debe ejecutar la Partida Automática de Cierre Diario nuevamente.
          </Alert>
        )}

        {loading || loadingTipos ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Cargando transacciones...</Typography>
          </Box>
        ) : (
          <>
            {transaccionesActuales && transaccionesActuales.length > 0 ? (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                      <TableCell sx={{ fontWeight: 'bold', width: 50 }}></TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Tipo Transacción</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Número Documento</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Concepto</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>Valor</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Fecha</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Usuario</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {agruparTransacciones(transaccionesActuales).map((grupo) => (
                      <React.Fragment key={grupo.idTipo}>
                        {/* Fila de grupo */}
                        <TableRow 
                          sx={{ 
                            backgroundColor: '#f5f5f5',
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: '#e0e0e0' }
                          }}
                          onClick={() => toggleGrupo(grupo.idTipo)}
                        >
                          <TableCell>
                            <IconButton size="small">
                              {gruposExpandidos[grupo.idTipo] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </TableCell>
                          <TableCell colSpan={3}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                              {grupo.descripcion}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {grupo.detalles.length} transaccion(es)
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                              {formatNumber(grupo.total)}
                            </Typography>
                          </TableCell>
                          <TableCell colSpan={2}></TableCell>
                        </TableRow>
                        
                        {/* Detalles expandibles */}
                        <TableRow>
                          <TableCell colSpan={7} sx={{ p: 0, border: 0 }}>
                            <Collapse in={gruposExpandidos[grupo.idTipo]} timeout="auto" unmountOnExit>
                              <Table size="small">
                                <TableBody>
                                  {grupo.detalles.map((transaccion) => {
                                    const index = transaccion.indexOriginal;
                                    const tipoTransaccionId = parseInt(transaccion.IdTipoTransaccion || transaccion.idTipoTransaccion);
                                    
                                    return (
                                      <TableRow key={transaccion.Id || transaccion.id || index} sx={{ backgroundColor: '#fafafa' }}>
                                        <TableCell sx={{ width: 50 }}></TableCell>
                                        <TableCell>
                                          {editando ? (
                                            <FormControl fullWidth size="small">
                                              <Select
                                                value={tipoTransaccionId || ''}
                                                onChange={(e) => {
                                                  handleActualizarTransaccion(index, 
                                                    transaccion.IdTipoTransaccion !== undefined ? 'IdTipoTransaccion' : 'idTipoTransaccion',
                                                    e.target.value);
                                                }}
                                              >
                                                {tiposTransacciones.map((tipo) => {
                                                  const tipoId = parseInt(tipo.id || tipo.Id || tipo.idTipoTransaccion || tipo.IdTipoTransaccion);
                                                  return (
                                                    <MenuItem key={tipoId} value={tipoId}>
                                                      {tipo.descripcionTipoTransaccion || tipo.DescripcionTipoTransaccion}
                                                    </MenuItem>
                                                  );
                                                })}
                                              </Select>
                                            </FormControl>
                                          ) : (
                                            <Typography variant="body2">
                                              {transaccion.DescripcionTipoTransaccion || transaccion.descripcionTipoTransaccion || 'N/A'}
                                            </Typography>
                                          )}
                                        </TableCell>
                                        <TableCell>
                                          <Typography variant="body2">
                                            {transaccion.NumeroDocumento || transaccion.numeroDocumento || '-'}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>
                                          <Typography variant="body2">
                                            {transaccion.Concepto || transaccion.concepto || '-'}
                                          </Typography>
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'right' }}>
                                          {editando ? (
                                            <TextField
                                              size="small"
                                              type="number"
                                              value={transaccion.Valor || transaccion.valor}
                                              onChange={(e) => handleActualizarTransaccion(index,
                                                transaccion.Valor !== undefined ? 'Valor' : 'valor',
                                                parseFloat(e.target.value))}
                                              sx={{ width: 120 }}
                                              InputProps={{
                                                startAdornment: 'L. '
                                              }}
                                            />
                                          ) : (
                                            <Typography variant="body2">
                                              {formatNumber(transaccion.Valor || transaccion.valor)}
                                            </Typography>
                                          )}
                                        </TableCell>
                                        <TableCell>
                                          <Typography variant="body2">
                                            {formatDate(transaccion.FechaTransaccion || transaccion.fechaTransaccion || 
                                                       transaccion.FechaIngreso || transaccion.fechaIngreso)}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>
                                          <Typography variant="body2">
                                            {(transaccion.IngresadoPor || transaccion.ingresadoPor || 'N/A').trim()}
                                          </Typography>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                  </TableBody>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#e8f5e9' }}>
                      <TableCell colSpan={4}><strong>TOTAL DETALLE SISTEMA:</strong></TableCell>
                      <TableCell sx={{ textAlign: 'right' }}>
                        <strong>
                          {formatNumber(transaccionesActuales.reduce((sum, t) => 
                            sum + parseFloat(t.Valor || t.valor || 0), 0
                          ))}
                        </strong>
                        {editando && !validarTotalTransacciones() && (
                          <Typography color="error" variant="caption" display="block">
                            ⚠️ El total no coincide con el original
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell colSpan={2}></TableCell>
                    </TableRow>
                  </TableHead>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No hay transacciones registradas para este cierre
                </Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        {!editando && transaccionesActuales.length > 0 && (
          <Button
            startIcon={<EditIcon />}
            onClick={handleIniciarEdicion}
            variant="outlined"
            color="primary"
            disabled={loading || loadingTipos || !tienePermiso('Editar Transacción Bancaria')}
          >
            Editar Transacciones
          </Button>
        )}
        
        {editando ? (
          <>
            <Button
              startIcon={<CancelIcon />}
              onClick={handleCancelarEdicion}
              variant="outlined"
            >
              Cancelar
            </Button>
            <Button
              startIcon={<SaveIcon />}
              onClick={handleGuardarCambios}
              variant="contained"
              color="success"
              disabled={!tienePermiso('Editar Transacción Bancaria')}
            >
              Guardar Cambios
            </Button>
          </>
        ) : (
          <Button
            startIcon={<CloseIcon />}
            onClick={onClose}
            variant="contained"
          >
            Cerrar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ModalEditarTransacciones;
